import { all, call, delay, put, select } from 'redux-saga/effects'
import { t } from 'i18next'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import {
  selectActiveAddress,
  selectActiveAddressIsDirty,
  selectActiveElectricityMeterIsDirty,
  selectActiveGasMeterIsDirty,
  selectAddressesConfigData,
  selectClientTabHasUnsavedChanges,
  selectContactIsDirty,
  selectCurrentAddressData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateConfig } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { saveContact } from '@/containers/SubscriptionFormPageNew/Components/Contact/ContactModuleSagas'
import { saveAddress } from '@/containers/SubscriptionFormPageNew/Components/Address/AddressModuleSagas'
import { isDraftAddressKey } from '@/containers/SubscriptionFormPageNew/Components/Address/draftAddress'
import { saveMeterShared } from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSaveSharedSagas'
import {
  isElectricityMeterDataReady,
  isGasMeterDataReady,
} from '@/containers/SubscriptionFormPageNew/Components/shared/meterSaveGating'

export const saveClientTabActions = {
  CLIENT_TAB_SAVE_ALL: 'CLIENT_TAB_SAVE_ALL',
}

/**
 * Orchestrates the four client-tab saves.
 *
 * Ordering matters here:
 *   1. Contact and Address run in parallel (independent slices of state).
 *   2. Once Address is persisted, the two Meter saves run in parallel.
 *      We pass `skipAddressSave: true` so saveMeterShared never re-issues
 *      a saveAddress call ─ which would otherwise duplicate the address
 *      when the orchestrator has just saved it (the address bucket is
 *      'new' and saveMeterShared's existing fallback would fire again).
 *
 * Each step also:
 *   - Only fires the endpoints whose module is dirty, scoped to the ACTIVE
 *     address (selectActiveAddressIsDirty / selectActiveElectricityMeterIsDirty
 *     / selectActiveGasMeterIsDirty). This matches the original per-module
 *     save buttons which only ever saved the active address context.
 *   - Skips dirty modules whose original save button would be disabled
 *     (e.g. PDL with invalid eligibility) and reports it via snackbar.
 */
const SAVED_STATUS_DURATION_MS = 5000

export function* saveClientTab({ enqueue }) {
  const { country } = yield select(selectAuthData)

  // Scope dirty checks to the active address. The orchestrator only ever
  // operates on selectActiveAddress / selectCurrentAddressData, so using
  // scoped selectors keeps the dirty state and the actual save consistent.
  const contactDirty = yield select(selectContactIsDirty)
  const addressDirty = yield select(selectActiveAddressIsDirty)
  const elecDirty = yield select(selectActiveElectricityMeterIsDirty)
  const gasDirty = yield select(selectActiveGasMeterIsDirty)

  if (!contactDirty && !addressDirty && !elecDirty && !gasDirty) {
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.WARNING,
        message: t('subscriptionForm:snackbar.client_tab.nothing_to_save'),
      })
    }
    return
  }

  const activeAddress = yield select(selectActiveAddress)
  const currentAddressData = yield select(selectCurrentAddressData)
  const { addressIsSaving } = yield select(selectAddressesConfigData)
  const { electricityMeter = {}, gasMeter = {} } = currentAddressData

  // ── Phase 1: contact + address in parallel ─────────────────────────────
  // We track each task slot so we can read saveAddress's success flag and
  // decide whether Phase 2 should skip the meter's saveAddress fallback.
  const phase1 = []
  let addressTaskIndex = -1

  if (contactDirty) {
    phase1.push(call(saveContact, { enqueue }))
  }

  if (addressDirty) {
    if (addressIsSaving) {
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.WARNING,
          message: t('subscriptionForm:snackbar.client_tab.skipped_address'),
        })
      }
    } else {
      addressTaskIndex = phase1.length
      phase1.push(
        call(saveAddress, {
          enqueue,
          successMessage: t('subscriptionForm:address_v2.address_saved_success'),
        }),
      )
    }
  }

  // Run phase1 in parallel and capture results. `yield all([...])` returns
  // the array of values returned by each saga, preserving the order of input.
  const phase1Results = phase1.length > 0 ? yield all(phase1) : []

  // ── Phase 2: meters in parallel, only after Address is persisted ────────
  // Only set skipAddressSave when saveAddress actually returned success.
  // saveAddress can return early on 4xx without throwing, so we MUST check.
  //
  // When the active address was a draft ('new' / null) and saveAddress just
  // persisted it, the backend returns a real numeric id. We MUST forward
  // that id to saveMeterShared, otherwise the meter payload would be sent
  // with addressId='new' (or null) and the meter save would fail.
  let skipAddressSave = false
  let resolvedAddressId = activeAddress
  if (addressTaskIndex >= 0) {
    const addressResult = phase1Results[addressTaskIndex]
    if (addressResult?.success === true) {
      skipAddressSave = true
      // saveAddress returns the persisted backend id (or the existing one).
      // Normalize null → 'new' so saveMeterShared keeps its existing
      // semantics when no real id is available.
      resolvedAddressId = addressResult.addressId ?? 'new'
    }
  }

  if (isDraftAddressKey(activeAddress) && !skipAddressSave && (elecDirty || gasDirty)) {
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.WARNING,
        message: t('subscriptionForm:snackbar.client_tab.skipped_meters'),
      })
    }
    return
  }

  const phase2 = []

  if (elecDirty) {
    if (!isElectricityMeterDataReady({ electricityMeter, country })) {
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.WARNING,
          message: t('subscriptionForm:snackbar.client_tab.skipped_pdl'),
        })
      }
    } else {
      phase2.push(
        call(saveMeterShared, {
          enqueue,
          meterData: electricityMeter,
          addressId: resolvedAddressId,
          meterType: 'electricity',
          skipAddressSave,
        }),
      )
    }
  }

  if (gasDirty) {
    if (!isGasMeterDataReady({ gasMeter, country })) {
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.WARNING,
          message: t('subscriptionForm:snackbar.client_tab.skipped_pce'),
        })
      }
    } else {
      phase2.push(
        call(saveMeterShared, {
          enqueue,
          meterData: gasMeter,
          addressId: resolvedAddressId,
          meterType: 'gas',
          skipAddressSave,
        }),
      )
    }
  }

  if (phase2.length > 0) {
    yield all(phase2)
  }

  const stillHasUnsavedChanges = yield select(selectClientTabHasUnsavedChanges)
  if (!stillHasUnsavedChanges) {
    yield put(updateConfig({ clientTabJustSaved: true }))
    yield delay(SAVED_STATUS_DURATION_MS)
    yield put(updateConfig({ clientTabJustSaved: false }))
  }
}
